import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Button,
  CloseIcon,
  DatePicker,
  DragDropFileListLocal,
  Drawer,
  Input,
  InputNumber,
  Label,
  Select,
  showToast,
  Text,
} from '@mercai/clever'

import {
  getFileFromUrl,
  objectToFormData,
  useValidationErrors,
} from '@/helpers'
import {
  QUERIES_ADMIN,
  useFileSign,
  useShipmentInvoiceDetail,
  useShipmentInvoiceEdit,
} from '@/services'
import {
  BanksEnum,
  LogisticShipmentProps,
  SaleOrderProps,
  ShipmentInvoicePaymentPriorityEnum,
  ShipmentInvoiceProps,
  ShipmentInvoiceStatusEnum,
  ShipmentInvoiceTypeEnum,
  TransportCompanyProps,
} from '@/types'

import { useQueryClient } from '@tanstack/react-query'

import { SaleOrderSearch, TransportCompanySearch } from '@/components'
import { schema } from './drawer-form.constants'

import { ShipmentInvoiceDrawerFormProps } from './drawer-form.type'

import { shipmentInvoiceStatusMachine } from '../../helpers'
import styles from './drawer-form.module.css'

export const ShipmentInvoiceDrawerForm = ({
  onHandleOpen,
  uuid,
}: ShipmentInvoiceDrawerFormProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplierOrderManagementPage')

  const [values, setValues] = useState<Partial<ShipmentInvoiceProps>>({})

  const [salesOrderShpments, setSalesOrderShipments] = useState<
    LogisticShipmentProps[]
  >([])
  const [invoicePdfFile, setInvoicePdfFile] = useState<File[]>([])
  const [invoiceXmlFile, setInvoiceXmlFile] = useState<File[]>([])
  const [proofFile, setProofFile] = useState<File[]>([])

  const [invoicePdfFileWasEdited, setInvoicePdfFileWasEdited] = useState(false)
  const [invoiceXmlFileWasEdited, setInvoiceXmlFileWasEdited] = useState(false)
  const [proofFileWasEdited, setProofFileWasEdited] = useState(false)

  const { mutateAsync, isPending } = useShipmentInvoiceEdit(uuid)

  const showInvoiceTransactionInformation =
    values.status === 'paid' || values.status === 'cancelled'

  const { mutateAsync: mutateAsyncFileSign } = useFileSign()

  const getSignedUrl = async (url: string) => {
    if (!url) return ''

    if (!url.includes('http')) {
      const response = await mutateAsyncFileSign({
        filePath: url || '',
      })

      if (!response?.data?.signedUrl) return ''

      return response?.data?.signedUrl || ''
    }

    return url
  }

  const { data } = useShipmentInvoiceDetail(uuid)

  const { isValid, errors, onHandleListenErrors } = useValidationErrors(
    schema(t),
    values,
  )

  const queryClient = useQueryClient()

  const onSelectSaleOrders = (saleOrderParam: SaleOrderProps) => {
    const isSelected = salesOrderShpments.find(
      (saleOrder) => saleOrder.order === saleOrderParam.reference,
    )
    if (!isSelected) {
      setSalesOrderShipments([
        ...salesOrderShpments,
        saleOrderParam.shipments?.[0],
      ])
    }
  }

  const onRemoveSaleOrders = (saleOrderReference: string) => {
    const isSelected = salesOrderShpments.find(
      (saleOrder) => saleOrder.order === saleOrderReference,
    )
    if (isSelected) {
      setSalesOrderShipments(
        salesOrderShpments.filter((s) => s.order !== saleOrderReference),
      )
    }
  }

  const onHandleValues = (key: string, value?: string | number | null) => {
    setValues({ ...values, [key]: value })
  }

  const onHandleTransactionValues = (
    key: string,
    value?: string | number | null,
  ) => {
    setValues({
      ...values,
      transactions: [{ ...values.transactions?.[0], [key]: value }],
    })
  }

  const onClose = () => {
    onHandleOpen()
    setValues({})
    setInvoicePdfFile([])
    setInvoiceXmlFile([])
    setProofFile([])
    setInvoicePdfFileWasEdited(false)
    setInvoiceXmlFileWasEdited(false)
    setProofFileWasEdited(false)
    setSalesOrderShipments([])
  }

  const onSubmit = async () => {
    if (!isValid) {
      onHandleListenErrors(true)
      return
    }

    onHandleListenErrors(false)

    const payload = objectToFormData({
      ...values,
      createdAt: undefined,
      updatedAt: undefined,
      reference: undefined,
      uuid: undefined,
      transportCompany: undefined,
      transportCompanyUuid: values?.transportCompany?.uuid,
      shipments: undefined,
      invoicePdfFile: undefined,
      invoiceXmlFile: undefined,
      proofPdfFile: undefined,
      transactions:
        values?.transactions && values?.transactions?.length > 0
          ? values?.transactions?.map((val) =>
              JSON.stringify({ ...val, proofPdfFile: undefined }),
            )
          : undefined,
      shipmentsUuid:
        salesOrderShpments?.length > 0
          ? salesOrderShpments.map((s) => s.uuid)
          : '',
    })

    if (invoicePdfFileWasEdited) {
      payload.append(
        'invoicePdfFile',
        invoicePdfFile.length > 0 ? invoicePdfFile[0] : '',
      )
    }

    if (invoiceXmlFileWasEdited) {
      payload.append(
        'invoiceXmlFile',
        invoiceXmlFile.length > 0 ? invoiceXmlFile[0] : '',
      )
    }

    if (proofFileWasEdited) {
      payload.append(
        'shipmentTransactionProofPdfFile',
        proofFile.length > 0 ? proofFile[0] : '',
      )
    }

    const response = await mutateAsync(payload)

    if (response.success) {
      onClose()
      setValues({})
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.BFF_SHIPMENT_INVOICE.module],
      })
      showToast({
        position: 'bottom-left',
        variant: 'success',
        title: t('tabShipmentInvoiceDrawerFormEditSuccessTitle'),
        content: t('tabShipmentInvoiceDrawerFormEditSuccessDescription'),
      })
      return null
    }

    showToast({
      position: 'bottom-left',
      variant: 'error',
      content:
        response.message ||
        t('tabShipmentInvoiceDrawerFormEditSuccessDescription'),
    })
  }

  const downloadFileFromUrl = async (
    url: string,
  ): Promise<File | undefined> => {
    try {
      const signedUrl = await getSignedUrl(url)

      const file = await getFileFromUrl(signedUrl)

      return file
    } catch (error) {
      console.error(error)
    }
  }

  const populateValues = async (dataParam: ShipmentInvoiceProps) => {
    setValues(dataParam)
    setSalesOrderShipments(dataParam.shipments?.map((val) => val) || [])

    if (dataParam?.invoicePdfFile) {
      const file = await downloadFileFromUrl(dataParam.invoicePdfFile)
      if (file) setInvoicePdfFile([file])
    }

    if (dataParam?.invoiceXmlFile) {
      const file = await downloadFileFromUrl(dataParam.invoiceXmlFile)
      if (file) setInvoiceXmlFile([file])
    }

    if (dataParam?.transactions?.[0]?.proofPdfFile) {
      const file = await downloadFileFromUrl(
        dataParam.transactions?.[0]?.proofPdfFile,
      )
      if (file) setProofFile([file])
    }
  }

  useEffect(() => {
    if (data?.data) {
      populateValues(data?.data)
    }
  }, [data?.data])

  return (
    <Drawer isOpen={!!uuid} onHandleOpen={onClose} className={styles.drawer}>
      <div className={styles.header}>
        <div>
          <Text variant="title-large" className={styles['header-title']}>
            {t('tabShipmentInvoiceDrawerFormTitle')}
          </Text>

          <Text variant="text-small" className={styles['header-description']}>
            {t('tabShipmentInvoiceDrawerFormDescription')}
          </Text>
        </div>

        <button
          className={styles['header-button']}
          type="button"
          onClick={onHandleOpen}
        >
          <CloseIcon />
        </button>
      </div>

      <div className={styles.content}>
        <div>
          <Label required>{t('tabShipmentInvoiceDrawerFormStatusLabel')}</Label>
          <Select
            allowEmpty={false}
            value={values?.status}
            options={Object.keys(ShipmentInvoiceStatusEnum)
              .filter((key) =>
                shipmentInvoiceStatusMachine[
                  data?.data?.status || 'created'
                ].includes(key as keyof typeof ShipmentInvoiceStatusEnum),
              )
              .map((key) => ({
                label: tEnums(`shipment_invoice_status_${key}`),
                value: key,
              }))}
            onChange={(status) => onHandleValues('status', status)}
          />
        </div>

        <div>
          <Label required>
            {t('tabShipmentInvoiceDrawerRequestNewShipmentInvoiceTypeLabel')}
          </Label>
          <Select
            allowEmpty
            value={values?.type}
            options={Object.keys(ShipmentInvoiceTypeEnum)
              .filter((val) => val !== 'unknown')
              .map((key) => ({
                label: tEnums(`shipment_invoice_type_${key}`),
                value: key,
              }))}
            onChange={(type) => onHandleValues('type', type)}
            error={errors?.type as string}
          />
        </div>

        <div>
          <Label required>
            {t('tabShipmentInvoiceDrawerFormPaymentPriorityLabel')}
          </Label>
          <Select
            allowEmpty={false}
            value={values?.paymentPriority}
            options={Object.keys(ShipmentInvoicePaymentPriorityEnum).map(
              (key) => ({
                label: tEnums(`shipment_invoice_payment_priority_${key}`),
                value: key,
              }),
            )}
            onChange={(paymentPriority) =>
              onHandleValues('paymentPriority', paymentPriority)
            }
          />
        </div>

        {showInvoiceTransactionInformation && (
          <>
            <div>
              <Label>
                {t('tabShipmentInvoiceDrawerFormTransactionPaymentMethodLabel')}
              </Label>
              <Select
                value={values.transactions?.[0]?.destinationBank}
                options={Object.keys(BanksEnum).map((key) => ({
                  label: key,
                  value: key,
                }))}
                onChange={(destinationBank) =>
                  onHandleTransactionValues('destinationBank', destinationBank)
                }
                placeholder={t('selectPlaceholder')}
              />
            </div>

            <div>
              <Label>
                {t('tabShipmentInvoiceDrawerFormTransactionPaidAtLabel')}
              </Label>
              <DatePicker
                value={values?.transactions?.[0]?.paidAt}
                onChange={(paidAt) =>
                  onHandleTransactionValues('paidAt', paidAt)
                }
                placeholder="DD/MM/AAAA"
              />
            </div>

            <div>
              <Label>
                {t('tabShipmentInvoiceDrawerFormTransactionProofFileLabel')}
              </Label>
              <DragDropFileListLocal
                translations={{
                  dragFile: t('uploadDragFile'),
                  clickToUpload: t('uploadClickToUpload'),
                  sizeFile: t('uploadSizeFile'),
                }}
                data={proofFile}
                onDrop={(file) => {
                  setProofFile(file)
                  setProofFileWasEdited(true)
                }}
                onRemove={(file) => {
                  setProofFile(file)
                  setProofFileWasEdited(true)
                }}
              />
            </div>
          </>
        )}

        <div>
          <Label required>
            {t('tabShipmentInvoiceDrawerFormTransportCompanyLabel')}
          </Label>
          <TransportCompanySearch
            title={' '}
            selected={values?.transportCompany}
            onSelect={(transportCompany) =>
              setValues({
                ...values,
                transportCompany: transportCompany as TransportCompanyProps,
                transportCompanyUuid: transportCompany?.uuid,
              })
            }
            error={errors?.transportCompanyUuid as string}
          />
        </div>

        <div>
          <Label required>{t('tabShipmentInvoiceDrawerFormValueLabel')}</Label>
          <div className={styles.row}>
            <Select
              allowEmpty={false}
              value={values.currency}
              options={[
                { label: 'USD', value: 'USD' },
                { label: 'MXN', value: 'MXN' },
              ]}
              onChange={(currency) => onHandleValues('currency', currency)}
            />
            <div style={{ flex: 1 }}>
              <InputNumber
                value={values?.value}
                decimalPrecision={2}
                onChange={(value) => onHandleValues('value', value)}
                showIcons
                variant={errors?.value ? 'danger' : 'primary'}
              />
            </div>
          </div>
          {errors?.value && (
            <Text className={styles.error}>{errors.value as string}</Text>
          )}
        </div>

        <div>
          <Label>
            {t(
              'tabShipmentInvoiceDrawerRequestNewShipmentInvoiceSaleOrdersLabel',
            )}
          </Label>
          <div>
            <SaleOrderSearch
              title={' '}
              onSelect={(value) => onSelectSaleOrders(value as SaleOrderProps)}
            />
            <div className={styles['sale-orders']}>
              {salesOrderShpments.map((saleOrder) => (
                <div className={styles['sale-order']} key={saleOrder.reference}>
                  <Text variant="text-small">{saleOrder.order}</Text>
                  <CloseIcon
                    className={styles['sale-order-remove-icon']}
                    onClick={() => onRemoveSaleOrders(saleOrder.order)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <Label>
            {t('tabShipmentInvoiceDrawerFormExpectedPaymentDateLabel')}
          </Label>
          <DatePicker
            value={values?.expectedPaymentDate}
            onChange={(expectedPaymentDate) =>
              onHandleValues('expectedPaymentDate', expectedPaymentDate)
            }
            placeholder="DD/MM/AAAA"
          />
        </div>

        <div>
          <Label>{t('tabShipmentInvoiceDrawerFormObservationLabel')}</Label>
          <Input
            value={values?.observation}
            onChange={(observation) =>
              onHandleValues('observation', observation)
            }
          />
        </div>

        <div>
          <Label>
            {t('tabShipmentInvoiceDrawerFormInvoiceExternalReferenceLabel')}
          </Label>
          <Input
            value={values?.invoiceExternalReference}
            onChange={(invoiceExternalReference) =>
              onHandleValues(
                'invoiceExternalReference',
                invoiceExternalReference,
              )
            }
          />
        </div>

        <div>
          <Label>{t('tabShipmentInvoiceDrawerFormInvoicePdfFileLabel')}</Label>
          <DragDropFileListLocal
            translations={{
              dragFile: t('uploadDragFile'),
              clickToUpload: t('uploadClickToUpload'),
              sizeFile: t('uploadSizeFile'),
            }}
            data={invoicePdfFile}
            onDrop={(file) => {
              setInvoicePdfFile(file)
              setInvoicePdfFileWasEdited(true)
            }}
            onRemove={(file) => {
              setInvoicePdfFile(file)
              setInvoicePdfFileWasEdited(true)
            }}
          />
        </div>

        <div>
          <Label>{t('tabShipmentInvoiceDrawerFormInvoiceXmlFileLabel')}</Label>
          <DragDropFileListLocal
            translations={{
              dragFile: t('uploadDragFile'),
              clickToUpload: t('uploadClickToUpload'),
              sizeFile: t('uploadSizeFile'),
            }}
            data={invoiceXmlFile}
            onDrop={(file) => {
              setInvoiceXmlFile(file)
              setInvoiceXmlFileWasEdited(true)
            }}
            onRemove={(file) => {
              setInvoiceXmlFile(file)
              setInvoiceXmlFileWasEdited(true)
            }}
          />
        </div>
      </div>

      <div className={styles.footer}>
        <Button variant="secondary" onClick={onClose}>
          {t('drawerCancel')}
        </Button>
        <Button onClick={onSubmit} disabled={isPending} isActive={!isPending}>
          {t('drawerSave')}
        </Button>
      </div>
    </Drawer>
  )
}
